<!--
  Role Switcher dropdown. Wraps the role text under the user's name
  in the header. Click to open a popover listing every role available
  to the user (CURRENT_AVAILABLE_ROLES, populated by the StatusIndicator
  probe). Pick one → write to users/{uid}.snowflakeConnections.<id>.activeRole
  via the globals store, which fires the StatusIndicator's watch and
  re-probes immediately so the rest of the UI flips to the new role.

  Design notes:
    - Read-only when there are no roles available (e.g. probe hasn't
      completed, or user has no live connection). Shows "—" instead of
      a clickable button.
    - Highlights the role currently active in the live session. Useful
      because the active session role might differ from the persisted
      activeRole field (e.g. just-connected, the override hasn't been
      applied yet, or Snowflake auto-promoted a default).
    - Clicking the same role that's already active is a no-op: writing
      a duplicate value to Firestore wouldn't trigger a probe refresh
      anyway since the watcher early-returns on equal values.
    - "Use default" entry at the top — clears `activeRole` so Snowflake
      uses the user's DEFAULT_ROLE on next connect.
-->

<template>
  <div class="relative" v-click-outside="close">
    <button
      type="button"
      class="text-xs text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center gap-1"
      :class="{ 'cursor-default hover:text-gray-500 dark:hover:text-gray-400': !canSwitch }"
      :disabled="!canSwitch"
      @click="toggle"
    >
      <span>{{ displayRole }}</span>
      <svg v-if="canSwitch" class="w-3 h-3 transition-transform" :class="{ 'rotate-180': open }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute right-0 top-full z-50 mt-1.5 w-64 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1E2130] shadow-xl ring-1 ring-black/5 overflow-hidden"
      >
        <!-- ─── OAuth informational view ─────────────────────────
             OAuth tokens bind to a single primary role and the
             session is "restricted" — USE ROLE / role-override are
             blocked. But the integration's IMPLICIT secondary-role
             setting auto-activates every granted role as a secondary,
             so privilege-wise the user already has access to all of
             them. We show that here as a transparent state of the
             world rather than a broken switcher. -->
        <template v-if="isOAuthSession">
          <div class="border-b border-gray-200 dark:border-white/10 px-3 py-2">
            <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Snowflake Roles
            </h4>
            <p class="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
              Active primary: <span class="font-mono text-gray-700 dark:text-gray-300">{{ sessionRole || '—' }}</span>
            </p>
          </div>

          <div class="px-3 py-3 space-y-2">
            <p class="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">
              OAuth logins inherit all your granted roles as secondary
              roles — privilege checks already run with the union of
              every role below. The primary role only affects display
              (CURRENT_ROLE) and ownership on new objects.
            </p>

            <div class="rounded-md border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02]">
              <div class="px-2.5 py-1.5 border-b border-gray-200 dark:border-white/10">
                <span class="text-[9px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Inherited via secondary roles
                </span>
              </div>
              <ul class="max-h-48 overflow-y-auto py-1">
                <li
                  v-for="role in availableRoles"
                  :key="role"
                  class="px-2.5 py-1 flex items-center justify-between gap-2 text-[11px]"
                >
                  <span class="font-mono text-gray-900 dark:text-gray-100">{{ role }}</span>
                  <span v-if="role === sessionRole" class="text-[9px] text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                    primary
                  </span>
                </li>
                <li v-if="!availableRoles.length" class="px-2.5 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 italic">
                  No roles loaded yet. Connect to Snowflake to populate.
                </li>
              </ul>
            </div>

            <p class="text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
              To change which role appears as primary, sign out and sign
              back in via Snowflake OAuth — Snowflake binds OAuth tokens
              to a single primary role per session.
            </p>
          </div>
        </template>

        <!-- ─── Non-OAuth interactive switcher ──────────────────
             For PAT / password / keypair users, the snowflake
             connector accepts a `role=` arg and switches cleanly
             on each connect. The Firestore-persisted override
             reaches both the browser path (this UI) and the
             backend session probe. -->
        <template v-else>
          <div class="border-b border-gray-200 dark:border-white/10 px-3 py-2">
            <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Switch Snowflake Role
            </h4>
            <p v-if="activeRoleOverride" class="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
              Persisted across sessions until you switch again.
            </p>
          </div>

          <div class="max-h-72 overflow-y-auto py-1">
            <!-- Use default — clears the override, lets Snowflake fall
                 back to the user's DEFAULT_ROLE. -->
            <button
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              :class="{ 'bg-purple-50 dark:bg-purple-900/20': !activeRoleOverride }"
              @click="pick('')"
            >
              <span class="text-gray-700 dark:text-gray-200 italic">Use default</span>
              <svg v-if="!activeRoleOverride" class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
            </button>

            <div class="my-1 h-px bg-gray-200 dark:bg-white/10" />

            <button
              v-for="role in availableRoles"
              :key="role"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              :class="{ 'bg-purple-50 dark:bg-purple-900/20': role === activeRoleOverride }"
              @click="pick(role)"
            >
              <span class="font-mono text-gray-900 dark:text-gray-100">{{ role }}</span>
              <span v-if="role === sessionRole && role !== activeRoleOverride" class="text-[9px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                live
              </span>
              <svg v-if="role === activeRoleOverride" class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
            </button>

            <p v-if="!availableRoles.length" class="px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400 italic">
              No roles available. Connect to Snowflake to load your role list.
            </p>
          </div>

          <div v-if="busy" class="border-t border-gray-200 dark:border-white/10 px-3 py-2 text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Switching role…
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const open = ref(false)
const busy = ref(false)

const globalsStore = useGlobalsStore()
const { availableSnowflakeRoles, snowflakeSession, activeSnowflakeRole, userProfile } = storeToRefs(globalsStore)

const availableRoles = computed(() => availableSnowflakeRoles.value || [])
// `activeSnowflakeRole` is the persisted override (Firestore-backed,
// empty when "use default"). Distinct from `sessionRole` which is
// CURRENT_ROLE() from the most recent probe — the two converge after
// a probe but diverge briefly between role switch and re-probe.
const activeRoleOverride = computed(() => activeSnowflakeRole.value)
const sessionRole = computed(() => snowflakeSession.value?.role || '')

// Detect OAuth sessions. Snowflake OAuth tokens bind to a single
// primary role; USE ROLE is blocked, body-level role overrides are
// rejected unless the role matches the token's scope. So for OAuth
// users we can't actually swap primary roles per-query — but with
// OAUTH_USE_SECONDARY_ROLES = IMPLICIT the session inherits ALL the
// user's granted roles as secondary roles, so privilege-wise they
// can already read/write objects under any of those roles. The
// switcher's UI for OAuth users is informational rather than
// interactive: show what's inherited, explain the limitation.
const isOAuthSession = computed(() => {
  return ((userProfile.value as any)?.activeSnowflakeConnection?.connectionType || '').toLowerCase() === 'oauth'
})

// Display label: prefer the live session role (matches what the
// StatusIndicator chip and hover card show), fall back to the
// persisted override, fall back to "—" when no Snowflake context
// is available at all. The Pulsar-membership role label
// (Owner/Admin/etc.) is reserved for the parent layout's fallback
// when there's no Snowflake session at all.
const displayRole = computed(() => {
  return sessionRole.value || activeRoleOverride.value || (userProfile.value?.roleLabel || 'Member')
})

// Whether the chip is interactive at all (controls the caret + the
// click-to-open behavior). Both OAuth and non-OAuth sessions get an
// interactive chip when there's at least one role known — for OAuth
// users the popover is informational (lists inherited roles +
// explains the model), for everyone else it's the real switcher.
const canSwitch = computed(() => {
  return availableRoles.value.length > 0
})

function toggle() {
  if (!canSwitch.value) return
  open.value = !open.value
}

function close() {
  open.value = false
}

async function pick(role: string) {
  // Empty string = "use default" → clears the override.
  // Same role → no-op, just close the popover.
  if (role === activeRoleOverride.value) {
    close()
    return
  }
  busy.value = true
  try {
    await globalsStore.setActiveSnowflakeRole(role)
    // The StatusIndicator's watch on activeSnowflakeRole picks up the
    // Firestore sync → triggers a probe → snowflakeSession.role
    // updates → header re-renders. We don't need to wait here.
  } catch (e) {
    console.error('Failed to switch role:', e)
    globalsStore.alertSnackbar(
      `Failed to switch role: ${(e as Error)?.message || 'unknown error'}`,
      'error',
    )
  } finally {
    busy.value = false
    close()
  }
}

// Inline click-outside directive without pulling in @vueuse/core.
// Lightweight enough that wiring it as a plugin would be overkill;
// a single mounted component using it.
const vClickOutside = {
  mounted(el: HTMLElement, binding: any) {
    el._clickOutside = (event: MouseEvent) => {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value?.()
      }
    }
    document.addEventListener('mousedown', el._clickOutside)
  },
  unmounted(el: any) {
    document.removeEventListener('mousedown', el._clickOutside)
    delete el._clickOutside
  },
}

// Belt-and-suspenders Escape handling — Vue's `v-on:keydown.esc`
// won't fire on a non-focused element, so listen at document level.
function onEscape(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) close()
}
onMounted(() => document.addEventListener('keydown', onEscape))
onBeforeUnmount(() => document.removeEventListener('keydown', onEscape))
</script>
